import type { LookupTransferRequestResponse } from '@getstep/proto/transfer'
import { addAmounts } from '@getstep/sdk/dist/http/models/Amount'
import { PublicTransferStore } from '@getstep/sdk/dist/store/PublicTransferStore'
import { splitNames } from '@getstep/sdk/dist/util/Names'
import { isFuture } from 'date-fns'
import { makeAutoObservable } from 'mobx'
import type { NextRouter } from 'next/router'

import { PaymentIntentStore } from './PaymentIntentStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import { formatExpiresAt } from '@lib/utils/date'
import { formatAmount, formatAsAmount } from '@lib/utils/number'
import { extendConsole } from '@shared/logging/console'

const logger = extendConsole('TransferRequestPageStore')

/**
 * Store for working with send/request money landing pages.
 */
export class TransferRequestPageStore {
    /**
     * Indicates the store is loading something
     */
    loading = false

    /**
     * Formatted expiration date.
     */
    expiresAt = ''

    /**
     * Deals with direct payment.
     */
    paymentIntent?: PaymentIntentStore

    constructor(
        private sdk: SdkClientStore,
        private transfer: LookupTransferRequestResponse,
    ) {
        this.updateExpiresAt()

        if (!transfer.request) return

        const restored = PaymentIntentStore.restore(sdk)

        if (restored && restored.transferRequestId !== transfer.request.id) {
            logger.warn(
                'restored payment intent does not match current transfer request',
            )
            PaymentIntentStore.clearStorage()
            return
        }

        this.paymentIntent = restored

        makeAutoObservable(this)
    }

    get expiresAtMs() {
        const { expiresAtMs } = this.request ?? {}
        return expiresAtMs && +expiresAtMs
    }

    get request() {
        return this.transfer.request
    }

    /**
     * If false, this request is no longer actionable.
     * It could be expired, fulfilled, or cancelled.
     */
    get pending() {
        const {
            transfer: { status },
            expiresAtMs,
            expiresAt, // this is here so the components update when the formatted date changes
        } = this

        return !!(
            status === 'PENDING' &&
            expiresAt &&
            expiresAtMs &&
            isFuture(+expiresAtMs)
        )
    }

    /**
     * Formats amount and currency for display.
     */
    get amountFormatted() {
        const { request: { amount } = {} } = this.transfer
        return formatAsAmount(amount?.value, amount?.currency)
    }

    /**
     * Adds fees to the amount.
     * Formats amount and currency for display.
     * TODO (@cataline): calculate this on backend.
     */
    get totalAmountFormatted() {
        const { paymentIntent, request } = this

        if (!request || !paymentIntent?.fee) {
            return this.amountFormatted
        }

        return formatAmount(addAmounts(paymentIntent.fee, request.amount))
    }

    /**
     * Something to be put into PersonalizationProvider
     */
    get personalizations() {
        const {
            expiresAt,
            request,
            amountFormatted,
            totalAmountFormatted,
            paymentIntent,
        } = this

        if (!request) return { expiresAt }

        const {
            requester: { pictureUrl, displayName },
        } = request

        return {
            firstName: splitNames(displayName).firstName,
            fullName: displayName,
            name: displayName,
            amountFormatted,
            pictureUrl: pictureUrl,
            totalAmountFormatted,
            ...paymentIntent?.personalizations,
        }
    }

    static async findTransferRequest(sdk: SdkClientStore, id: string) {
        return PublicTransferStore.findTransferRequest(
            id,
            await sdk.createPublicApi(),
        )
    }

    /**
     * Updates expiration date. To be called every second.
     */
    async updateExpiresAt() {
        const { expiresAtMs } = this
        if (!expiresAtMs) return
        this.expiresAt = formatExpiresAt(+expiresAtMs)
    }

    /**
     * Loads and saves new PaymentIntentStore
     */
    async createPaymentIntent(router: NextRouter): Promise<void> {
        try {
            this.loading = true

            if (this.paymentIntent) {
                this.paymentIntent.start(router)
                return
            }

            PaymentIntentStore.clearStorage()

            if (!this.request) {
                throw new Error('Transfer request is missing!')
            }

            const paymentIntent = await PaymentIntentStore.create(
                this.sdk,
                this.request,
            )
            this.paymentIntent = paymentIntent
            this.paymentIntent.start(router)
        } finally {
            this.loading = false
        }
    }
}
